/**
 * Space Lifecycle Commands - Space-level operations
 *
 * Handles creating, switching, and listing spaces, coordinating between
 * systemStore and navigation.
 *
 * Following ENTITY_STATE_ARCHITECTURE pattern.
 */

import { SelfContainedCommand, CommandExecutionContext } from '../../types/commands';
import { StandardToolResult, createSuccessResult, createErrorResult } from '../../types/standardToolResults';
import { createFileLogger } from '../../utils/logger';
import useSystemStore from '../../stores/systemStore';

const log = createFileLogger('SPACE_LIFECYCLE_COMMANDS', { enabled: false });

export const spaceCreateCommand: SelfContainedCommand = {
  id: 'space_create',
  description: 'Create a new space in the current dataspace (personal or organization)',
  autonomyLevel: 'auto',
  visualTreatment: 'demonstrative',

  schema: {
    parameters: [
      {
        name: 'name',
        type: 'string',
        description: 'Name for the new space',
        required: true,
        validator: (value: string) => {
          if (!value || value.trim().length === 0) return 'Space name cannot be empty';
          if (value.length > 100) return 'Space name too long (max 100 chars)';
          return null;
        },
        examples: ['My Workspace', 'Project Alpha', 'Research Notes']
      },
      {
        name: 'templateType',
        type: 'string',
        description: 'Optional template type to initialize the space with',
        required: false,
        examples: ['blank', 'research', 'project']
      }
    ]
  },

  examples: {
    good: [
      {
        input: { name: 'My Workspace' },
        expectedOutput: createSuccessResult('Space created', { spaceId: 'space_123' }),
        description: 'Create a new blank space',
        context: 'When user wants a new workspace'
      }
    ],
    bad: [
      {
        input: { name: '' },
        error: 'Space name cannot be empty',
        why: 'Empty names are not valid',
        fix: 'Provide a meaningful space name'
      }
    ]
  },

  performance: {
    expectedDuration: 500,
    complexity: 'medium'
  },

  handler: async (params: any, _context: CommandExecutionContext): Promise<StandardToolResult> => {
    const startTime = Date.now();
    const { name, templateType } = params;

    try {
      log.info('[space_create] Creating space:', { name, templateType });

      const newSpace = await useSystemStore.getState().actions.createSpace({
        name,
        templateType
      });

      log.info('[space_create] ✅ Space created:', {
        spaceId: newSpace.id,
        name: newSpace.name
      });

      return createSuccessResult(
        `✅ Space created: ${newSpace.name}`,
        {
          spaceId: newSpace.id,
          space: {
            id: newSpace.id,
            name: newSpace.name,
            org_id: newSpace.org_id
          }
        },
        {
          metadata: {
            executionTime: Date.now() - startTime
          }
        }
      );
    } catch (error: any) {
      log.error('[space_create] Failed to create space:', error);
      return createErrorResult(
        'Failed to create space',
        error.message || 'Unknown error',
        'execution',
        {
          metadata: { executionTime: Date.now() - startTime }
        }
      );
    }
  }
};

export const spaceSwitchCommand: SelfContainedCommand = {
  id: 'space_switch',
  description: 'Switch to a different space (must be in same dataspace)',
  autonomyLevel: 'auto',
  visualTreatment: 'demonstrative',

  schema: {
    parameters: [
      {
        name: 'spaceId',
        type: 'string',
        description: 'ID of the space to switch to',
        required: true,
        examples: ['space_123', 'space_456']
      }
    ]
  },

  examples: {
    good: [
      {
        input: { spaceId: 'space_123' },
        expectedOutput: createSuccessResult('Switched to space', { spaceId: 'space_123' }),
        description: 'Switch to another space',
        context: 'When user wants to change workspace'
      }
    ],
    bad: []
  },

  performance: {
    expectedDuration: 200,
    complexity: 'low'
  },

  handler: async (params: any, _context: CommandExecutionContext): Promise<StandardToolResult> => {
    const startTime = Date.now();
    const { spaceId } = params;

    try {
      log.info('[space_switch] Switching to space:', { spaceId });

      useSystemStore.getState().actions.switchSpace(spaceId);

      log.info('[space_switch] ✅ Switched to space:', spaceId);

      return createSuccessResult(
        `✅ Switched to space`,
        { spaceId },
        {
          metadata: {
            executionTime: Date.now() - startTime
          }
        }
      );
    } catch (error: any) {
      log.error('[space_switch] Failed to switch space:', error);
      return createErrorResult(
        'Failed to switch space',
        error.message || 'Unknown error',
        'execution',
        {
          metadata: { executionTime: Date.now() - startTime }
        }
      );
    }
  }
};

export const spaceListCommand: SelfContainedCommand = {
  id: 'space_list',
  description: 'List recent spaces in the current dataspace',
  autonomyLevel: 'auto',
  visualTreatment: 'silent',

  schema: {
    parameters: []
  },

  examples: {
    good: [
      {
        input: {},
        expectedOutput: createSuccessResult('Spaces listed', { spaces: [] }),
        description: 'List all spaces',
        context: 'When user wants to see available spaces'
      }
    ],
    bad: []
  },

  performance: {
    expectedDuration: 300,
    complexity: 'low'
  },

  handler: async (_params: any, _context: CommandExecutionContext): Promise<StandardToolResult> => {
    const startTime = Date.now();

    try {
      log.info('[space_list] Listing spaces');

      await useSystemStore.getState().actions.refreshRecentSpaces();

      const spaces = useSystemStore.getState().getCurrentDataspaceSpaces();

      log.info('[space_list] ✅ Spaces listed:', spaces.length);

      return createSuccessResult(
        `✅ Found ${spaces.length} space${spaces.length !== 1 ? 's' : ''}`,
        {
          spaces: spaces.map(s => ({
            id: s.id,
            name: s.name,
            org_id: s.org_id,
            updatedAt: s.updatedAt
          })),
          count: spaces.length
        },
        {
          metadata: {
            executionTime: Date.now() - startTime
          }
        }
      );
    } catch (error: any) {
      log.error('[space_list] Failed to list spaces:', error);
      return createErrorResult(
        'Failed to list spaces',
        error.message || 'Unknown error',
        'execution',
        {
          metadata: { executionTime: Date.now() - startTime }
        }
      );
    }
  }
};

export const spaceLifecycleCommands: SelfContainedCommand[] = [
  spaceCreateCommand,
  spaceSwitchCommand,
  spaceListCommand,
];
